<script lang="ts">
	import { Modal, ButtonV2, Toggle, FlexibleCard, Container } from '@Shelly/shelly-design-system';
	import SelectList from '../../SelectList/SelectList.svelte';
	import type { Card, ListType } from '../../SelectList/SelectList.types';
	import { format } from 'svelte-i18n';
	import { createEventDispatcher } from 'svelte';
	import { createMemberStatusSnapshot } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { deriveRoomName } from 'src/helpers/service_rooms_helpers';
	import { sortRoomsWithMissingDevices } from '../../Lists/list-helpers';

	export let showModal: boolean = false;
	export let cameras: string[] = []; // Ordered selected camera IDs from parent
	export let hideOfflineCameras: boolean = false;

	const dispatch = createEventDispatcher<{
		close: void;
		save: { cameras: string[]; hideOfflineCameras: boolean };
	}>();

	let allCardsRefs: { [key: string | number]: HTMLElement } = {};

	// Local state for editing (reset when modal opens)
	let localCameras: string[] = [];
	let localHideOffline: boolean = false;

	// Reset local state when modal opens
	$: if (showModal) {
		localHideOffline = hideOfflineCameras;
		// If cameras is empty, select all by default
		localCameras = cameras.length > 0 ? [...cameras] : allCameraDevices.map((d) => d.id);
	}

	// Get all camera devices from store
	$: allCameraDevices = Object.values($devices || {}).filter(
		(device) => device?.category === 'camera',
	);
	$: cameraIds = allCameraDevices.map((d) => d.id);
	$: memberStatusStore = createMemberStatusSnapshot(cameraIds);
	$: memberStatus = $memberStatusStore;

	// Check if a camera is online
	function isCameraOnline(cameraId: string): boolean {
		const status = memberStatus[cameraId];
		return status?.online ?? false;
	}

	// Convert local cameras to array format expected by SelectList
	$: localCamerasSet = new Set(localCameras);
	$: selectedCardsIdArray = localCameras.map((id) => ({ id }));

	// Group cameras by room (same pattern as DevicesSelectionStep)
	$: camerasByRoom = filterCamerasByRoomID(allCameraDevices);

	function filterCamerasByRoomID(cameraDevices: typeof allCameraDevices): ListType {
		let filteredByRoom: Record<
			string | number,
			{
				cards: Card[];
				roomName: string;
				roomID: string | number;
				position?: number;
			}
		> = {};

		cameraDevices.forEach((camera) => {
			const roomID = camera.room_id ?? -20;
			const isOnline = isCameraOnline(camera.id);
			const offlineLabel = $format('label_offline', { default: 'Offline' });
			const displayName = !isOnline ? `${camera.name} - ${offlineLabel}` : camera.name;

			const cameraData: Card = {
				id: camera.id,
				name: displayName,
				roomName: deriveRoomName(roomID),
				roomID: roomID,
				image: camera.image,
				icon: camera.icon,
				backgroundColor: camera.backgroundColor,
			};

			if (filteredByRoom[roomID]) {
				filteredByRoom[roomID].cards = [...filteredByRoom[roomID].cards, cameraData];
			} else {
				filteredByRoom[roomID] = {
					cards: [cameraData],
					roomName: deriveRoomName(roomID),
					roomID: roomID,
					position: $rooms[roomID]?.position,
				};
			}
		});

		return sortRoomsWithMissingDevices(filteredByRoom) as ListType;
	}

	function handleClose() {
		dispatch('close');
	}

	function handleSave() {
		// Dispatch save with current local state
		// If all cameras selected, save as empty array (= all)
		const allIds = allCameraDevices.map((d) => d.id);
		const isAllSelected =
			localCameras.length === allIds.length && allIds.every((id) => localCamerasSet.has(id));

		dispatch('save', {
			cameras: isAllSelected ? [] : localCameras,
			hideOfflineCameras: localHideOffline,
		});
		dispatch('close');
	}

	function handleCameraSelect(camera: Card) {
		const cameraId = String(camera.id);
		if (localCamerasSet.has(cameraId)) {
			localCameras = localCameras.filter((id) => id !== cameraId);
		} else {
			localCameras = [...localCameras, cameraId];
		}
	}

	function handleToggleHideOffline() {
		localHideOffline = !localHideOffline;
	}

	// Count selected cameras
	$: selectedCount = localCameras.length;
</script>

<Modal
	{showModal}
	on:close={handleClose}
	title={$format('title_camera_dashboard_settings', {
		default: 'Customize Dashboard',
	})}
>
	<svelte:fragment slot="content">
		<Container padding="0">
			<FlexibleCard
				backgroundColor={$showNewLayout ? '--color-0' : undefined}
				class="mb-m"
				padding="s"
			>
				<Toggle
					id="hide-offline"
					checked={localHideOffline}
					size="m"
					on:click={handleToggleHideOffline}
				>
					{$format('toggle_hide_offline_cameras', {
						default: 'Hide offline cameras',
					})}
				</Toggle>
			</FlexibleCard>
		</Container>

		<Container padding="0">
			<SelectList
				title={$format('label_cameras_selected', {
					default: 'Cameras:',
				})}
				titleHighlight={selectedCount}
				list={camerasByRoom}
				variant="checkbox"
				selectedCardsId={selectedCardsIdArray}
				bind:allCardsRefs
				on:click={(e) => handleCameraSelect(e.detail)}
			/>
		</Container>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end gap-s">
		<ButtonV2
			size="m"
			variant="secondary"
			on:click={handleClose}
			aria-label={$format('button_cancel', { default: 'Cancel' })}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			size="m"
			variant="primary"
			on:click={handleSave}
			aria-label={$format('button_save', { default: 'Save' })}
		>
			{$format('button_save', { default: 'Save' })}
		</ButtonV2>
	</div>
</Modal>
